import type { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useWeekViewContext, type WeekOfDate } from "../../contexts/WeekView/WeekViewContext";
import { useDepartmentFlags } from "../../hooks/useDepartmentFlags";
import { useFormatPersonName } from "../../hooks/useNameFormat";
import type { Nurse, NurseInShift } from "../../utils/models";
import {
	groupNurseShiftsForDay,
	type QuickAddConflict,
	QuickAddConflictKind,
	resolveQuickAddConflict,
	type ShiftWindow,
	singleBlockingShift,
} from "../../utils/scheduleConflict";
import { buildConflictHint } from "../../utils/scheduleConflictCopy";

const NO_CONFLICT: QuickAddConflict = { kind: QuickAddConflictKind.None };

/** The day's scheduled shifts by person; empty in actual mode, where the server refuses no overlap to predict. */
const dayShiftsByNurse = (dayWeek: WeekOfDate | undefined, showActual: boolean): Map<number, NurseInShift[]> => {
	if (showActual) return new Map();
	// `dayIndex` is a position in the week's first row, so that row's day gives the date every row is matched by.
	const dayDate = dayWeek?.week.weeklyShifts[0]?.days[dayWeek.dayIndex]?.date;
	// DEFAULT: a day the shared week doesn't hold has nothing to predict against. Not reached with the "+" on screen:
	// the Day view renders only once the shared week holds its day (`DaySchedule`). If it ever were, the server still
	// refuses the overlap (409) and the add's error snackbar says so.
	if (!dayWeek || !dayDate) return new Map();
	return groupNurseShiftsForDay(dayWeek.week.weeklyShifts, dayDate);
};

type Params = {
	date: DateTime;
	showActual: boolean;
	nurse: Nurse | null;
	window: ShiftWindow;
	/** The destination for the shift the current times clash with (`undefined` when none or several do). */
	locationFor: (blockingShift: NurseInShift | undefined) => string | null;
};

/**
 * What the "+" Add Staff dialog should say and allow for the picked person, given who is already on the day's
 * schedule: the day's shifts by person (which the staff picker lists one row per shift from), and the
 * destination and intent (add / double / move / blocked) for the current times.
 */
export const useQuickAddScheduleConflict = ({ date, showActual, nurse, window, locationFor }: Params) => {
	const { t } = useTranslation();
	const { timezone } = useDepartmentFlags();
	const { weekOfDate } = useWeekViewContext();
	const formatPersonName = useFormatPersonName();
	const shiftsByNurse = useMemo(() => dayShiftsByNurse(weekOfDate(date), showActual), [weekOfDate, date, showActual]);
	// DEFAULT: no entry in the day's map means the person has no shift that day — a plain add, nothing to predict.
	const sameDayShifts = nurse ? shiftsByNurse.get(nurse.id) : undefined;
	// The clash is found before the destination (it does not depend on one), so the destination can follow it.
	const blockingShift = useMemo(() => (sameDayShifts ? singleBlockingShift(sameDayShifts, window) : undefined), [sameDayShifts, window]);
	const location = locationFor(blockingShift);
	const conflict = useMemo<QuickAddConflict>(
		() => (sameDayShifts ? resolveQuickAddConflict({ sameDayShifts, window, location }) : NO_CONFLICT),
		[sameDayShifts, window, location],
	);

	const hint = useMemo(
		() => (nurse ? buildConflictHint({ conflict, name: formatPersonName(nurse), location, t, timezone }) : null),
		[conflict, nurse, location, t, timezone, formatPersonName],
	);
	const isBlocked = conflict.kind === QuickAddConflictKind.SameLocation || conflict.kind === QuickAddConflictKind.MultipleOverlap;
	return { shiftsByNurse, location, conflict, hint, isBlocked };
};
